// Global Types
import type {
  $FullAdminRegatta,
  $FullRegattaEntry,
  $FullRegattaEntryRequirement,
  $FullRegattaGroup,
  $FullRegattaResultTable,
  $FullRegattaResultTableEntry,
  $FullRegattaResultTableRace,
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  docRoute,
  docType,
} from 'common.enums';
import {
  RegattaRequirementType,
  RegattaResultTableRaceType,
  RegattaResultTableType,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Link,
} from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  Row,
  Table,
} from 'reactstrap';
import {
  CommonDialog,
  DeleteDialog,
  FormDialog,
  InputCheckbox,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  cleanFormValues,
  isDiff,
  numberValue,
} from '@vroskus/library-helpers/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  getRegattaEntryRequirementValue,
  getRegattaEntryTitle,
  isNumericPosition,
  isResolutionPosition,
  isResolutionWithPointsPosition,
  shortenText,
} from 'common.helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  getDocumentUrl,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import InputResult from '../../../form/InputResult';
import ResultPosDialog from '../../../dialog/ResultPosDialog';
import EmptyTable from '../../../common/EmptyTable';
import RegattaEntry from '../../../common/RegattaEntry';
import RegattaResultTableFields from '../../../fields/RegattaResultTableFields';
import RegattaResultTableRaceFields from '../../../fields/RegattaResultTableRaceFields';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  data: Array<$FullRegattaResultTable>;
  recalcRegattaResultTableRacePositions: {
    keyResult: $Model<'RegattaResultTableRaceResult'>;
    regattaResultTableRace: $FullRegattaResultTableRace;
    revert: boolean;
  } | null;
  resourceDataRegattaEntry: Array<$FullRegattaEntry>;
  resourceDataRegattaGroup: Array<$FullRegattaGroup>;
};

type $PositionRecalc = {
  entry: $FullRegattaResultTableEntry;
  id: string;
  nextPosition: number;
  prevPosition: number;
};

class RegattaResultTablesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      recalcRegattaResultTableRacePositions: null,
      resourceDataRegattaEntry: [],
      resourceDataRegattaGroup: [],
    };
  }

  componentDidMount(): void {
    this.getData();
    this.getResourceData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
      this.getResourceData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        const models = ['RegattaResultTable', 'RegattaResultTableEntry', 'RegattaResultTableRace'];

        if (item.id === data.RegattaId && _.includes(
          models,
          data.modelName,
        )) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          const data = await Services.Api.getManyRegattaResultTable({
            RegattaId,
          });

          await this.setStateAsync({
            data,
          });
        }
      },
    });
  }

  async getResourceData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          const resourceDataRegattaGroup = await Services.Api.getResourceRegattaGroup({
            RegattaId,
          });
          const resourceDataRegattaEntry = await Services.Api.getResourceRegattaEntry({
            RegattaId,
          });

          await this.setStateAsync({
            resourceDataRegattaEntry,
            resourceDataRegattaGroup,
          });
        }
      },
    });
  }

  async updateResult({
    regattaResultTableId,
    regattaResultTableRaceId,
    result,
  }: {
    regattaResultTableId: string;
    regattaResultTableRaceId: string;
    result: $Model<'RegattaResultTableRaceResult'>;
  }): Promise<void> {
    const {
      data,
    } = this.state;

    const regattaResultTableIndex = _.findIndex(
      data,
      {
        id: regattaResultTableId,
      },
    );

    const regattaResultTable = data[regattaResultTableIndex];

    const regattaResultTableRaceIndex = _.findIndex(
      regattaResultTable.RegattaResultTableRaces,
      {
        id: regattaResultTableRaceId,
      },
    );

    const regattaResultTableRace = regattaResultTable
      .RegattaResultTableRaces[regattaResultTableRaceIndex];

    const regattaResultTableRaceResultIndex = _.findIndex(
      regattaResultTableRace.RegattaResultTableRaceResults,
      {
        id: result.id,
      },
    );

    const updatedData = data;

    updatedData[regattaResultTableIndex]
      .RegattaResultTableRaces[regattaResultTableRaceIndex]
      .RegattaResultTableRaceResults[regattaResultTableRaceResultIndex] = result;

    await this.setStateAsync({
      data: updatedData,
    });
  }

  async checkResultPositionUpdate({
    nextResult,
    prevResult,
    regattaResultTableRace,
  }: {
    nextResult: $Model<'RegattaResultTableRaceResult'>;
    prevResult: $Model<'RegattaResultTableRaceResult'>;
    regattaResultTableRace: $FullRegattaResultTableRace;
  }): Promise<void> {
    const isPrevResultResolution: boolean = isResolutionPosition({
      Position: prevResult.Position,
    }) !== null;
    const isNextResultResolution: boolean = isResolutionPosition({
      Position: nextResult.Position,
    }) !== null;

    // 3 -> DNF
    if (isPrevResultResolution === false && isNextResultResolution === true) {
      await this.setStateAsync({
        recalcRegattaResultTableRacePositions: {
          keyResult: prevResult,
          regattaResultTableRace,
          revert: false,
        },
      });
    }

    // DNF -> 3
    if (isPrevResultResolution === true && isNextResultResolution === false) {
      await this.setStateAsync({
        recalcRegattaResultTableRacePositions: {
          keyResult: nextResult,
          regattaResultTableRace,
          revert: true,
        },
      });
    }
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;
    const {
      data,
      resourceDataRegattaGroup,
    } = this.state;

    const RegattaId = item.id;

    const newRegattaResultTableItem = {
      Discards: 0,
      Name: '',
      Public: false,
      RegattaGroupId: '',
      RegattaId,
      StartGroup: null,
      Type: RegattaResultTableType.boat,
    };

    const unapprovedRaces: Array<$FullRegattaResultTableRace> = [];

    data.forEach((regattaResultTable: $FullRegattaResultTable) => {
      regattaResultTable.RegattaResultTableRaces.forEach((
        regattaResultTableRace: $FullRegattaResultTableRace,
      ) => {
        if (regattaResultTableRace.Approved === false) {
          unapprovedRaces.push(regattaResultTableRace);
        }
      });
    });

    return (
      <div className={'bg-white rounded'}>
        <SectionHeader
          bottomBorder={data.length === 0}
          iconClass={'icon-user'}
          rightButtons={(
            <>
              {unapprovedRaces.length > 0 && (
                <Button
                  className={'mr-1'}
                  color={'secondary'}
                  id={'regatta-result-table-approve-all-button'}
                  onClick={async () => {
                    const actions: Array<Promise<unknown>> = [];

                    unapprovedRaces.forEach((regattaResultTableRace) => {
                      const action = defaultUpsertItem({
                        action: async () => {
                          const params = {
                            ...regattaResultTableRace,
                            Approved: true,
                            id: regattaResultTableRace.id,
                          };

                          await Services.Api.upsertRegattaResultTableRace(params);
                        },
                        quiet: true,
                      });

                      actions.push(action);
                    });

                    await Promise.all(actions);
                  }}
                  outline
                  size={'sm'}
                >
                  <i className={'icon-check mr-2'} />
                  {t('Approve all')}
                </Button>
              )}
              <ResultPosDialog id={'regatta-result-table-instructions-dialog'}>
                <Button
                  className={'mr-1'}
                  color={'secondary'}
                  id={'regatta-result-table-instructions-button'}
                  outline
                  size={'sm'}
                >
                  <em className={'far fa-circle-exclamation mr-1'} />
                  {t('Instructions')}
                </Button>
              </ResultPosDialog>
              <Link
                className={'mr-1 btn btn-outline-secondary btn-sm'}
                title={t('Regatta overall results')}
                to={'/regatta/result_overalls'}
              >
                <em className={'fa icon-shuffle mr-2'} />
                {t('Overall results')}
              </Link>
              <FormDialog
                fields={(
                  <RegattaResultTableFields
                    item={newRegattaResultTableItem}
                    resourceData={resourceDataRegattaGroup}
                  />
                )}
                id={'create-regatta-result-table-dialog'}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$ModelUpsertParams<'RegattaResultTable'>>({
                      ...newRegattaResultTableItem,
                      ...values,
                      Discards: numberValue(values.Discards),
                    });

                    await Services.Api.upsertRegattaResultTable(params);
                  },
                })}
                subTitle={t('New')}
                title={t('Regatta result table')}
              >
                <Button
                  color={'info'}
                  id={'create-regatta-result-table-button'}
                  size={'sm'}
                  title={t('Add new regatta result table')}
                >
                  {t('Add new')}
                </Button>
              </FormDialog>
            </>
          )}
          title={t('Regatta result tables')}
        />
      </div>
    );
  }

  renderRegattaResultTableRacePositionsRecalcDialog() {
    const {
      item,
      t,
    } = this.props;
    const {
      data,
      recalcRegattaResultTableRacePositions,
    } = this.state;

    if (recalcRegattaResultTableRacePositions !== null) {
      const {
        keyResult,
        regattaResultTableRace,
        revert,
      } = recalcRegattaResultTableRacePositions;

      const positionRecalcs: Array<$PositionRecalc> = [];

      /* eslint-disable-next-line complexity */
      regattaResultTableRace.RegattaResultTableRaceResults.forEach((
        result: $Model<'RegattaResultTableRaceResult'>,
      ) => {
        const regattaResultTable: $FullRegattaResultTable | undefined = _.find(
          data,
          {
            id: regattaResultTableRace.RegattaResultTableId,
          },
        );

        const regattaResultTableEntries: Array<$FullRegattaResultTableEntry> = _.get(
          regattaResultTable,
          'RegattaResultTableEntries',
          [],
        );

        const entry: $FullRegattaResultTableEntry | undefined = _.find(
          regattaResultTableEntries,
          {
            id: result.RegattaResultTableEntryId,
          },
        );

        if (entry && result.Position && isNumericPosition({
          Position: result.Position,
        }) && isResolutionWithPointsPosition({
          Position: result.Position,
        })) {
          const resultPosition = Number(result.Position);
          const keyResultPosition = Number(keyResult.Position);

          const collidingResult: $Model<'RegattaResultTableRaceResult'> | undefined = _.find(
            regattaResultTableRace.RegattaResultTableRaceResults,
            {
              Position: keyResult.Position,
            },
          );

          // 3 -> DNF
          if (revert === false && resultPosition > keyResultPosition) {
            const positionRecalc: $PositionRecalc = {
              entry,
              id: result.id,
              nextPosition: resultPosition - 1,
              prevPosition: resultPosition,
            };

            positionRecalcs.push(positionRecalc);
          }

          // DNF -> 3
          if (revert === true
            && resultPosition >= keyResultPosition
            && result.id !== keyResult.id
            && collidingResult
          ) {
            const positionRecalc: $PositionRecalc = {
              entry,
              id: result.id,
              nextPosition: resultPosition + 1,
              prevPosition: resultPosition,
            };

            positionRecalcs.push(positionRecalc);
          }
        }
      });

      const sortedItems: Array<$PositionRecalc> = _.sortBy(
        positionRecalcs,
        'prevPosition',
      );

      return (
        <CommonDialog
          closeAction={() => this.setState({
            recalcRegattaResultTableRacePositions: null,
          })}
          content={(
            <CardBody>
              <Table>
                <thead>
                  <th>{t('Entry')}</th>
                  <th className={'text-center'}>
                    {t('Previous position')}
                  </th>
                  <th />
                  <th className={'text-center'}>
                    {t('New position')}
                  </th>
                </thead>
                <tbody>
                  {sortedItems.map(({
                    entry,
                    nextPosition,
                    prevPosition,
                  }) => (
                    <tr key={entry.id}>
                      <td>
                        <RegattaEntry
                          bowNoEnabled={item.BowNoEnabled}
                          countryEnabled={item.CountryEnabled}
                          item={entry.RegattaEntry}
                        />
                      </td>
                      <td className={'text-center'}>
                        {prevPosition}
                      </td>
                      <td>
                        <em className={'icon-arrow-right'} />
                      </td>
                      <td className={'text-center'}>
                        {nextPosition}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          )}
          id={'reorder-regatta-result-table-dialog'}
          instant
          labelCloseButton={t('Close')}
          labelConfirmButton={t('Confirm')}
          onAction={async () => {
            const actions: Array<Promise<unknown>> = [];

            sortedItems.forEach(({
              id,
              nextPosition,
            }) => {
              const action = defaultUpsertItem({
                action: async () => {
                  const params = {
                    id,
                    InputDate: new Date(),
                    Position: String(nextPosition),
                  };

                  await Services.Api.updateRegattaResultTableRaceResult(params);
                },
                quiet: true,
              });

              actions.push(action);
            });

            await Promise.all(actions);

            await this.getData();

            await this.setStateAsync({
              recalcRegattaResultTableRacePositions: null,
            });
          }}
          title={t('Reorder these positions?')}
        />
      );
    }

    return null;
  }

  renderAddRaceButton({
    regattaResultTable,
  }: {
    regattaResultTable: $FullRegattaResultTable;
  }) {
    const {
      t,
    } = this.props;

    const now: moment.Moment = moment();

    const newRegattaResultTableRaceItem = {
      Approved: false,
      Coef: 1,
      Course: null,
      Day: Number(now.format('D')),
      Distance: null,
      Dominant: false,
      Hour: 12,
      Minute: 0,
      Month: Number(now.format('M')),
      Name: '',
      Public: false,
      RegattaResultTableId: regattaResultTable.id,
      Second: 0,
      ShowPlaceColumn: true,
      ShowPointsColumn: false,
      ShowTimeColumn: false,
      Twa: null,
      Tws: null,
      Type: RegattaResultTableRaceType.monotype,
      Undiscard: false,
      Year: Number(now.format('YYYY')),
    };

    return (
      <FormDialog
        fields={(
          <RegattaResultTableRaceFields item={newRegattaResultTableRaceItem} />
        )}
        id={`create-regatta-result-table-race-dialog-${regattaResultTable.id}`}
        labelCloseButton={t('Close')}
        labelSaveButton={t('Save')}
        onAction={(values) => defaultUpsertItem({
          action: async () => {
            const params = cleanFormValues<$ModelUpsertParams<'RegattaResultTableRace'>>({
              ...newRegattaResultTableRaceItem,
              ...values,
              Coef: numberValue(values.Coef),
              Day: numberValue(values.Day),
              Hour: numberValue(values.Hour),
              Minute: numberValue(values.Minute),
              Month: numberValue(values.Month),
              Second: numberValue(values.Second),
              Year: numberValue(values.Year),
            });

            await Services.Api.upsertRegattaResultTableRace(params);
          },
        })}
        subTitle={t('New')}
        title={t('Regatta result table race')}
      >
        <Button
          color={'info'}
          id={`create-regatta-result-table-race-button-${regattaResultTable.id}`}
          size={'sm'}
        >
          {t('Add race')}
        </Button>
      </FormDialog>
    );
  }

  renderRegattaRequirementsButton(regattaEntry: $FullRegattaEntry) {
    const {
      item,
      t,
    } = this.props;

    return (
      <CommonDialog
        content={(
          <Table className={'mb-0'}>
            <tbody>
              {regattaEntry.RegattaEntryRequirements.map((
                regattaEntryRequirement: $FullRegattaEntryRequirement,
              ) => (regattaEntryRequirement.RegattaRequirement ? (
                <tr key={regattaEntryRequirement.id}>
                  <td>
                    <div>
                      {regattaEntryRequirement.RegattaRequirement.Name}
                    </div>
                    <small className={'text-muted'}>
                      {regattaEntryRequirement.RegattaRequirement.Content}
                    </small>
                  </td>
                  <td>
                    {_.includes(
                      [
                        RegattaRequirementType.fileSource,
                        RegattaRequirementType.file,
                      ],
                      regattaEntryRequirement.RegattaRequirement.Type,
                    ) ? (
                      <Button
                        color={'secondary'}
                        href={getRegattaEntryRequirementValue({
                          cdnUrl: config.CDN_URL,
                          regattaEntryRequirement,
                        })}
                        id={'regatta-entry-requirement-file-button'}
                        outline
                        size={'md'}
                        target={'_blank'}
                      >
                        <em className={'far fa-file'} />
                      </Button>
                      ) : (
                        <span>
                          {getRegattaEntryRequirementValue({
                            cdnUrl: config.CDN_URL,
                            regattaEntryRequirement,
                          })}
                        </span>
                      )}
                  </td>
                </tr>
              ) : null))}
            </tbody>
          </Table>
        )}
        id={`regatta-entry-requirements-dialog-${regattaEntry.id}`}
        labelCloseButton={t('Close')}
        labelConfirmButton={t('Confirm')}
        subTitle={getRegattaEntryTitle({
          bowNoEnabled: item.BowNoEnabled,
          countryEnabled: item.CountryEnabled,
          regattaEntry,
        })}
        title={t('Regatta entry requirements')}
      >
        <Button
          className={'mb-3'}
          id={`regatta-entry-requirements-button-${regattaEntry.id}`}
          size={'sm'}
          title={t('Regatta entry requirements')}
        >
          <em className={'icon-list'} />
        </Button>
      </CommonDialog>
    );
  }

  renderRegattaResultTableHeaderAddEntryButton(
    regattaResultTable: $FullRegattaResultTable,
  ) {
    const {
      item,
      t,
    } = this.props;
    const {
      resourceDataRegattaEntry,
    } = this.state;

    const regattaResultTableEntriesIds: Array<string> = regattaResultTable
      .RegattaResultTableEntries.map((
        regattaResultTableEntry,
      ) => regattaResultTableEntry.RegattaEntryId);

    const filteredResourceDataRegattaEntry: Array<$FullRegattaEntry> = resourceDataRegattaEntry
      .filter((resourceItem: $FullRegattaEntry) => !_.includes(
        regattaResultTableEntriesIds,
        resourceItem.id,
      ));

    const sortedResourceDataRegattaEntry: Array<$FullRegattaEntry> = _.sortBy(
      filteredResourceDataRegattaEntry,
      (resourceItem: $FullRegattaEntry) => _.get(
        resourceItem,
        'RegattaGroup.id',
      ) === regattaResultTable.RegattaGroupId,
    ).reverse();

    const groupedResourceDataRegattaEntry: Record<string, Array<$FullRegattaEntry>> = _.groupBy(
      sortedResourceDataRegattaEntry,
      (resourceItem: $FullRegattaEntry) => {
        const altName: null | string | undefined = _.get(
          resourceItem,
          'RegattaGroup.AltName',
        );

        const groupName: string = _.get(
          resourceItem,
          'RegattaGroup.Group.Name',
          t('Ungrouped'),
        );

        return altName || groupName;
      },
    );

    return (
      <FormDialog
        fields={(
          <>
            {filteredResourceDataRegattaEntry.length === 0 && (
              <EmptyTable />
            )}
            {_.map(
              groupedResourceDataRegattaEntry,
              (regattaEntries: Array<$FullRegattaEntry>, regattaGroupName: string) => (
                <div key={regattaGroupName}>
                  <h4 className={'mb-0 d-flex justify-content-between'}>
                    <span>{regattaGroupName}</span>
                  </h4>
                  <div className={'border-top pt-3'}>
                    {regattaEntries.map((regattaEntry: $FullRegattaEntry) => (
                      <div
                        className={'d-flex justify-content-between'}
                        key={regattaEntry.id}
                      >
                        <InputCheckbox
                          label={(
                            <RegattaEntry
                              bowNoEnabled={item.BowNoEnabled}
                              countryEnabled={item.CountryEnabled}
                              item={regattaEntry}
                              showAge
                            />
                          )}
                          name={regattaEntry.id}
                        />
                        {regattaEntry.RegattaEntryRequirements.length > 0
                          && this.renderRegattaRequirementsButton(regattaEntry)}
                      </div>
                    ))}
                  </div>
                </div>
              ),
            )}
          </>
        )}
        id={`create-regatta-result-table-entry-dialog-${regattaResultTable.id}`}
        labelCloseButton={t('Close')}
        labelSaveButton={t('Save')}
        onAction={(values) => defaultUpsertItem({
          action: async () => {
            const actions: Array<Promise<unknown>> = [];

            Object.keys(values).forEach((RegattaEntryId: string) => {
              if (values[RegattaEntryId]) {
                const params: $ModelUpsertParams<'RegattaResultTableEntry'> = {
                  Place: null,
                  Points: 0,
                  RegattaEntryId,
                  RegattaResultTableId: regattaResultTable.id,
                };

                const action = Services.Api.upsertRegattaResultTableEntry(params);

                actions.push(action);
              }
            });

            await Promise.all(actions);
          },
        })}
        size={'sm'}
        subTitle={'Add'}
        title={t('Regatta result table entries')}
      >
        <Button
          color={'info'}
          id={`create-regatta-result-table-entry-button-${regattaResultTable.id}`}
          size={'sm'}
        >
          {t('Add entries')}
        </Button>
      </FormDialog>
    );
  }

  renderRegattaResultTableHeader(regattaResultTable: $FullRegattaResultTable) {
    const {
      t,
    } = this.props;
    const {
      resourceDataRegattaGroup,
    } = this.state;

    return (
      <tr key={'header'}>
        <th
          className={'fix'}
          key={'addEntry'}
        >
          <div>
            {shortenText(
              regattaResultTable.Name,
              25,
            )}
          </div>
          <div className={'d-flex justify-content-between align-items-end'}>
            {this.renderRegattaResultTableHeaderAddEntryButton(regattaResultTable)}
            <div className={'text-right'}>
              <FormDialog
                fields={(
                  <RegattaResultTableFields
                    item={regattaResultTable}
                    resourceData={resourceDataRegattaGroup}
                  />
                )}
                id={`update-regatta-result-table-dialog-${regattaResultTable.id}`}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$Model<'RegattaResultTable'>>({
                      ...regattaResultTable,
                      ...values,
                      Discards: numberValue(values.Discards),
                      RegattaResultTableEntries: undefined,
                      RegattaResultTableRaces: undefined,
                    });

                    await Services.Api.upsertRegattaResultTable(params);
                  },
                })}
                subTitle={regattaResultTable.Name}
                title={t('Regatta result table')}
              >
                <Button
                  className={'mr-1'}
                  color={'warning'}
                  id={`update-regatta-result-table-button-${regattaResultTable.id}`}
                  size={'sm'}
                  title={t('Edit result table')}
                >
                  <i className={'icon-note'} />
                </Button>
              </FormDialog>
              <DeleteDialog
                content={t('Do you really want to delete?')}
                id={`delete-regatta-result-table-dialog-${regattaResultTable.id}`}
                labelCloseButton={t('Close')}
                labelDeleteButton={t('Delete')}
                onAction={() => defaultDeleteItem({
                  action: async () => Services.Api.deleteRegattaResultTable(regattaResultTable),
                })}
                subTitle={regattaResultTable.Name}
                title={t('Regatta result table')}
              >
                <Button
                  color={'danger'}
                  id={`delete-regatta-result-table-button-${regattaResultTable.id}`}
                  size={'sm'}
                  title={t('Delete result table')}
                >
                  <i className={'icon-trash'} />
                </Button>
              </DeleteDialog>
            </div>
          </div>
        </th>
        {regattaResultTable.RegattaResultTableRaces.map((
          regattaResultTableRace: $FullRegattaResultTableRace,
        ) => (
          <th
            className={'race-column'}
            key={regattaResultTableRace.id}
          >
            <div>
              {shortenText(
                regattaResultTableRace.Name,
                26,
              )}
            </div>
            <ButtonGroup className={'d-flex'}>
              {regattaResultTableRace.Public ? (
                <Button
                  className={'mr-1 w-100'}
                  color={'success'}
                  id={`unpublish-regatta-result-table-race-button-${regattaResultTable.id}`}
                  onClick={() => defaultUpsertItem({
                    action: async () => {
                      const params = {
                        ...regattaResultTableRace,
                        Public: false,
                      };

                      await Services.Api.upsertRegattaResultTableRace(params);
                    },
                  })}
                  size={'sm'}
                  title={t('Unpublish race')}
                >
                  <i className={'icon-eye'} />
                </Button>
              ) : (
                <Button
                  className={'mr-1 w-100'}
                  color={'success'}
                  id={`publish-regatta-result-table-race-button-${regattaResultTable.id}`}
                  onClick={() => defaultUpsertItem({
                    action: async () => {
                      const params = {
                        ...regattaResultTableRace,
                        Public: true,
                        RegattaResultTableRaceResults: undefined,
                      };

                      await Services.Api.upsertRegattaResultTableRace(params);
                    },
                  })}
                  outline
                  size={'sm'}
                  title={t('Publish race')}
                >
                  <i className={'icon-eye'} />
                </Button>
              )}
              {regattaResultTableRace.Approved ? (
                <Button
                  className={'mr-1 w-100'}
                  color={'success'}
                  id={`unapprove-regatta-result-table-race-button-${regattaResultTable.id}`}
                  onClick={() => defaultUpsertItem({
                    action: async () => {
                      const params = {
                        ...regattaResultTableRace,
                        Approved: false,
                        RegattaResultTableRaceResults: undefined,
                      };

                      await Services.Api.upsertRegattaResultTableRace(params);
                    },
                  })}
                  size={'sm'}
                  title={t('Unapproved race')}
                >
                  <i className={'icon-check'} />
                </Button>
              ) : (
                <Button
                  className={'mr-1 w-100'}
                  color={'success'}
                  id={`approve-regatta-result-table-race-button-${regattaResultTable.id}`}
                  onClick={() => defaultUpsertItem({
                    action: async () => {
                      const params = {
                        ...regattaResultTableRace,
                        Approved: true,
                        RegattaResultTableRaceResults: undefined,
                      };

                      await Services.Api.upsertRegattaResultTableRace(params);
                    },
                  })}
                  outline
                  size={'sm'}
                  title={t('Approved race')}
                >
                  <i className={'icon-check'} />
                </Button>
              )}
              <FormDialog
                fields={(
                  <RegattaResultTableRaceFields item={regattaResultTableRace} />
                )}
                id={`update-regatta-result-table-race-dialog-${regattaResultTable.id}`}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$ModelUpsertParams<'RegattaResultTableRace'>>({
                      ...regattaResultTableRace,
                      ...values,
                      Coef: numberValue(values.Coef),
                      Day: numberValue(values.Day),
                      Hour: numberValue(values.Hour),
                      Minute: numberValue(values.Minute),
                      Month: numberValue(values.Month),
                      RegattaResultTableRaceResults: undefined,
                      Second: numberValue(values.Second),
                      Year: numberValue(values.Year),
                    });

                    await Services.Api.upsertRegattaResultTableRace(params);
                  },
                })}
                subTitle={regattaResultTableRace.Name}
                title={t('Regatta result table race')}
              >
                <Button
                  className={'mr-1 w-100'}
                  color={'warning'}
                  id={`update-regatta-result-table-race-button-${regattaResultTable.id}`}
                  size={'sm'}
                  title={t('Edit race')}
                >
                  <i className={'icon-note'} />
                </Button>
              </FormDialog>
              <DeleteDialog
                content={t('Do you really want to delete?')}
                id={`delete-regatta-result-table-race-dialog-${regattaResultTable.id}`}
                labelCloseButton={t('Close')}
                labelDeleteButton={t('Delete')}
                onAction={() => defaultDeleteItem({
                  action: async () => Services.Api.deleteRegattaResultTableRace(
                    regattaResultTableRace,
                  ),
                })}
                subTitle={regattaResultTableRace.Name}
                title={t('Regatta result table race')}
              >
                <Button
                  className={'w-100'}
                  color={'danger'}
                  id={`delete-regatta-result-table-race-button-${regattaResultTable.id}`}
                  size={'sm'}
                  title={t('Delete race')}
                >
                  <i className={'icon-trash'} />
                </Button>
              </DeleteDialog>
            </ButtonGroup>
          </th>
        ))}
        <th
          className={'entry-column'}
          key={'newRace'}
        >
          <div>&nbsp;</div>
          <div>
            {this.renderAddRaceButton({
              regattaResultTable,
            })}
          </div>
        </th>
        <th
          className={'last-column'}
          key={'results'}
        >
          <div>
            <div className={'mb-2 text-right'}>
              <span>{`${t('Qty.')}: `}</span>
              <span className={'text-muted'}>
                {regattaResultTable.RegattaResultTableEntries.length}
              </span>
            </div>
            <Row className={'no-gutters'}>
              <Col
                className={'text-center text-muted'}
                xs={6}
              >
                {t('Pts')}
              </Col>
              <Col
                className={'text-center text-bold'}
                xs={6}
              >
                {t('Plc')}
              </Col>
            </Row>
          </div>
        </th>
      </tr>
    );
  }

  renderRegattaResultTableContent(regattaResultTable: $FullRegattaResultTable) {
    const {
      item,
      t,
    } = this.props;

    const sortedRegattaResultTableEntries: Array<$FullRegattaResultTableEntry> = _.orderBy(
      regattaResultTable.RegattaResultTableEntries,
      ['RegattaEntry.Name', 'RegattaEntry.RegistrationNo'],
      ['asc', 'asc'],
    );

    return (
      <>
        {sortedRegattaResultTableEntries.length === 0 && (
          <tr key={'addFirstEntry'}>
            <td className={'fix'} />
            <td
              className={'text-center text-muted'}
              colSpan={100}
            >
              {t('Add first regatta entry')}
            </td>
          </tr>
        )}
        {sortedRegattaResultTableEntries.map((
          regattaResultTableEntry: $FullRegattaResultTableEntry,
        ) => (
          <tr key={regattaResultTableEntry.id}>
            <td className={'py-1 fix d-flex align-items-center justify-content-between'}>
              <RegattaEntry
                bowNoEnabled={item.BowNoEnabled}
                countryEnabled={item.CountryEnabled}
                item={regattaResultTableEntry.RegattaEntry}
                shorten
                showAge
              />
              <DeleteDialog
                content={t('Do you really want to delete?')}
                id={`delete-regatta-result-table-entry-dialog-${regattaResultTable.id}`}
                labelCloseButton={t('Close')}
                labelDeleteButton={t('Delete')}
                onAction={() => defaultDeleteItem({
                  action: async () => Services.Api.deleteRegattaResultTableEntry(
                    regattaResultTableEntry,
                  ),
                })}
                subTitle={regattaResultTableEntry.RegattaEntry.Name || ''}
                title={t('Regatta result table entry')}
              >
                <Button
                  color={'danger'}
                  id={`delete-regatta-result-table-entry-buton-${regattaResultTable.id}`}
                  size={'sm'}
                  title={t('Delete entry')}
                >
                  <i className={'icon-trash'} />
                </Button>
              </DeleteDialog>
            </td>
            {regattaResultTable.RegattaResultTableRaces.map((
              regattaResultTableRace: $FullRegattaResultTableRace,
            ) => (
              <React.Fragment key={regattaResultTableRace.id}>
                {regattaResultTableRace.RegattaResultTableRaceResults.filter((
                  result: $Model<'RegattaResultTableRaceResult'>,
                ) => result.RegattaResultTableEntryId === regattaResultTableEntry.id).map((
                  result: $Model<'RegattaResultTableRaceResult'>,
                ) => (
                  <td
                    className={`py-1 ${result.Discarded ? 'bg-light' : ''}`}
                    key={regattaResultTableRace.id}
                  >
                    <InputResult
                      item={result}
                      onChange={(values) => defaultUpsertItem({
                        action: async () => {
                          const params = cleanFormValues<$Model<'RegattaResultTableRaceResult'>>({
                            ...result,
                            ...values,
                            InputDate: new Date(),
                          });

                          const updatedResult = await Services.Api
                            .updateRegattaResultTableRaceResult(params);

                          await this.updateResult({
                            regattaResultTableId: regattaResultTable.id,
                            regattaResultTableRaceId: regattaResultTableRace.id,
                            result: updatedResult,
                          });

                          await this.checkResultPositionUpdate({
                            nextResult: updatedResult,
                            prevResult: result,
                            regattaResultTableRace,
                          });
                        },
                        quiet: true,
                      })}
                      raceType={regattaResultTableRace.Type}
                    />
                  </td>
                ))}
              </React.Fragment>
            ))}
            <td />
            <td className={'py-1 last-column'}>
              <div>
                <Row className={'no-gutters'}>
                  <Col
                    className={'text-center py-2 text-muted'}
                    xs={6}
                  >
                    {regattaResultTableEntry.Points}
                  </Col>
                  <Col
                    className={'text-center py-2 text-bold'}
                    xs={6}
                  >
                    {regattaResultTableEntry.Place}
                  </Col>
                </Row>
              </div>
            </td>
          </tr>
        ))}
      </>
    );
  }

  renderRegattaResultTableFooter(regattaResultTable: $FullRegattaResultTable) {
    const {
      t,
    } = this.props;

    return (
      <CardBody className={'text-right border-thin-top py-2'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={getDocumentUrl({
            cdnUrl: config.CDN_URL,
            docRoute: docRoute.adminRegattaResultTableEntries,
            docType: docType.pdf,
            id: regattaResultTable.id,
            language: i18next.language as $Language,
          })}
          id={`regatta-result-table-file-button-${regattaResultTable.id}`}
          outline
          size={'sm'}
          target={'_blank'}
        >
          <em className={'far fa-file-pdf mr-1'} />
          {t('Group entries list')}
        </Button>
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={getDocumentUrl({
            cdnUrl: config.CDN_URL,
            docRoute: docRoute.adminRegattaResultTable,
            docType: docType.pdf,
            id: regattaResultTable.id,
            language: i18next.language as $Language,
          })}
          id={`regatta-result-table-file-${regattaResultTable.id}`}
          outline
          size={'sm'}
          target={'_blank'}
        >
          <em className={'far fa-file-pdf'} />
          {t('Group results')}
        </Button>
      </CardBody>
    );
  }

  renderRegattaResultTable(
    regattaResultTable: $FullRegattaResultTable,
  ) {
    return (
      <Card
        className={'RegattaResultTable'}
        key={regattaResultTable.id}
      >
        <div className={'RegattaResultTableBody'}>
          <Table>
            <thead>
              {this.renderRegattaResultTableHeader(regattaResultTable)}
            </thead>
            <tbody>
              {this.renderRegattaResultTableContent(regattaResultTable)}
            </tbody>
          </Table>
        </div>
        {this.renderRegattaResultTableFooter(regattaResultTable)}
      </Card>
    );
  }

  renderContent() {
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <>
        {data.map((
          regattaResultTable: $FullRegattaResultTable,
        ) => this.renderRegattaResultTable(regattaResultTable))}
      </>
    );
  }

  render() {
    const {
      id,
    } = this.props;
    const {
      data,
    } = this.state;

    return (
      <Card
        id={id}
        style={{
          backgroundColor: data.length === 0 ? '#fff' : 'rgba(200,200,200,0.2)',
          borderWidth: 0,
        }}
      >
        {this.renderHeader()}
        {this.renderContent()}
        {this.renderRegattaResultTableRacePositionsRecalcDialog()}
      </Card>
    );
  }
}

export default RegattaResultTablesSectionView;
